<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Noise + glow BG layers -->
      <div class="bg-layer" aria-hidden="true">
        <div class="glow glow-1"></div>
        <div class="glow glow-2"></div>
        <div class="grid-lines"></div>
      </div>

      <!-- Nav -->
      <nav class="nav">
        <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="nav-logo" />
        <div class="nav-pill">
          <span class="pill-dot"></span>
          <span class="pill-text">{{ loName }} × {{ realtorName }}</span>
        </div>
      
      </nav>

      <!-- Body split -->
      <div class="body-split">

        <!-- Left: hero -->
        <section class="hero-col">
          <div class="eyebrow">
            <span class="ey-line"></span>
            <span class="ey-text">Mortgage Partnership</span>
          </div>
          <h1 class="hero-h1" v-html="heroH1Html"></h1>
          <p class="hero-p">{{ heroBody }}</p>
          <div class="hero-actions">
            <a class="btn-glow" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="btn-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
          <div class="contact-row">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="contact-badge">
              <span>📞</span> {{ loinfo.phone_number }}
            </a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="contact-badge">
              <span>✉</span> Email
            </a>
          </div>
        </section>

        <!-- Right: cards stacked -->
        <aside class="card-col">

          <div class="nova-card lo-card">
            <div class="nc-topline lo-topline"></div>
            <div class="nc-badge lo-badge">LO</div>
            <div class="nc-content">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="nc-img" />
              <div class="nc-info">
                <div class="nc-name">{{ loName }}</div>
                <div class="nc-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="nc-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="nc-divider"></div>
            <div class="nc-links">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="nc-link lo-link">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="nc-link lo-link">{{ loinfo.email }}</a>
            </div>
 
          </div>

          <div class="nova-card re-card">
            <div class="nc-topline re-topline"></div>
            <div class="nc-badge re-badge">RE</div>
            <div class="nc-content">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="nc-img" />
              <div class="nc-info">
                <div class="nc-name">{{ realtorName }}</div>
                <div class="nc-role">{{ realtor && realtor.brokerage_name }}</div>
              </div>
            </div>
            <div class="nc-divider re-divider"></div>
            <div class="nc-links">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="nc-link re-link">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="nc-link re-link">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="nc-link re-link" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>

        </aside>
      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandNova",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,700;1,400&display=swap');

.page {
  min-height: 100vh;
  background: #070b12;
  color: #e8edf5;
  font-family: 'Inter', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --bg: #070b12;
  --card-bg: rgba(255,255,255,0.04);
  --border: rgba(255,255,255,0.08);
  --muted: #8892a4;
  overflow: hidden;
  position: relative;
}

.root { position: relative; z-index: 1; display: flex; flex-direction: column; min-height: 100vh; }

/* BG */
.bg-layer { position: fixed; inset: 0; pointer-events: none; z-index: 0; }
.glow { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.35; }
.glow-1 { width: 500px; height: 500px; background: radial-gradient(circle, var(--a), transparent 70%); top: -100px; right: -100px; }
.glow-2 { width: 400px; height: 400px; background: radial-gradient(circle, color-mix(in srgb, var(--p) 80%, #3a6ff5), transparent 70%); bottom: -80px; left: -80px; }
.grid-lines {
  position: absolute; inset: 0;
  background-image: linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
  background-size: 60px 60px;
}

/* NAV */
.nav { display: flex; align-items: center; justify-content: space-between; padding: 20px 48px; border-bottom: 1px solid var(--border); backdrop-filter: blur(12px); position: relative; z-index: 10; }
.nav-logo { width: 140px; max-height:72px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.85; }
.nav-pill { display: flex; align-items: center; gap: 8px; background: var(--card-bg); border: 1px solid var(--border); border-radius: 999px; padding: 6px 14px; }
.pill-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--a); animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
.pill-text { font-size: 11px; color: var(--muted); }
.nav-cta { font-size: 12px; font-weight: 600; text-decoration: none; background: var(--a); color: #070b12; padding: 9px 20px; border-radius: 8px; letter-spacing: 0.02em; transition: opacity 0.2s; }
.nav-cta:hover { opacity: 0.85; }

/* BODY */
.body-split { display: grid; grid-template-columns: 1fr 360px; flex: 1; max-width: 1200px; margin: 0 auto; width: 100%; padding: 0 48px; gap: 48px; align-items: start; padding-top: 60px; }

/* HERO */
.hero-col { max-width: 560px; }
.eyebrow { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
.ey-line { width: 24px; height: 1.5px; background: var(--a); }
.ey-text { font-size: 10px; font-weight: 500; letter-spacing: 0.25em; text-transform: uppercase; color: var(--a); opacity: 0.85; }
.hero-h1 { font-family: 'Playfair Display', serif; font-size: clamp(38px, 5vw, 64px); font-weight: 700; line-height: 1.08; color: #f0f4fa; margin: 0 0 20px; }
.hero-h1 em { font-style: italic; color: var(--a); font-weight: 400; }
.hero-p { font-size: 14px; line-height: 1.78; color: var(--muted); margin-bottom: 32px; }
.hero-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 24px; }
.btn-glow {
  font-size: 13px; font-weight: 600; text-decoration: none;
  background: var(--a); color: #070b12;
  padding: 13px 28px; border-radius: 8px;
  box-shadow: 0 0 28px color-mix(in srgb, var(--a) 40%, transparent);
  transition: box-shadow 0.2s, transform 0.18s;
  width: 55%;
}
.btn-glow:hover { box-shadow: 0 0 44px color-mix(in srgb, var(--a) 55%, transparent); transform: translateY(-1px); }
.btn-ghost { font-size: 13px; font-weight: 400; text-decoration: none; color: var(--muted); border: 1px solid var(--border); padding: 13px 24px; border-radius: 8px; transition: border-color 0.18s, color 0.18s; }
.btn-ghost:hover { border-color: rgba(255,255,255,0.2); color: #e8edf5; }
.contact-row { display: flex; gap: 8px; flex-wrap: wrap; }
.contact-badge { font-size: 11px; text-decoration: none; color: var(--muted); background: var(--card-bg); border: 1px solid var(--border); padding: 6px 12px; border-radius: 6px; display: flex; align-items: center; gap: 6px; transition: border-color 0.18s; }
.contact-badge:hover { border-color: var(--a); color: var(--a); }

/* CARDS */
.card-col { display: flex; flex-direction: column; gap: 16px; padding-bottom: 48px; }
.nova-card { background: var(--card-bg); border: 1px solid var(--border); border-radius: 16px; padding: 20px; position: relative; overflow: hidden; backdrop-filter: blur(8px); transition: border-color 0.2s; }
.nova-card:hover { border-color: rgba(255,255,255,0.16); }
.nc-topline { position: absolute; top: 0; left: 0; right: 0; height: 2px; }
.lo-topline { background: linear-gradient(90deg, transparent, var(--p), transparent); }
.re-topline { background: linear-gradient(90deg, transparent, var(--a), transparent); }
.nc-badge { position: absolute; top: 14px; right: 14px; font-size: 9px; font-weight: 700; letter-spacing: 0.18em; border-radius: 4px; padding: 3px 7px; }
.lo-badge { background: color-mix(in srgb, var(--p) 25%, transparent); color: color-mix(in srgb, var(--p) 100%, #aab); border: 1px solid color-mix(in srgb, var(--p) 30%, transparent); }
.re-badge { background: color-mix(in srgb, var(--a) 18%, transparent); color: var(--a); border: 1px solid color-mix(in srgb, var(--a) 28%, transparent); }
.nc-content { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 14px; }
.nc-img { width: 52px; height: 52px; border-radius: 10px; object-fit: cover; flex-shrink: 0; border: 1px solid var(--border); }
.nc-name { font-size: 16px; font-weight: 600; color: #f0f4fa; }
.nc-role { font-size: 11px; color: var(--muted); margin-top: 2px; }
.nc-nmls { font-size: 10px; color: color-mix(in srgb, var(--muted) 60%, transparent); margin-top: 2px; }
.nc-divider { height: 1px; background: var(--border); margin: 12px 0; }
.re-divider { background: color-mix(in srgb, var(--a) 18%, transparent); }
.nc-links { display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px; }
.nc-link { font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.lo-link:hover { color: color-mix(in srgb, var(--p) 80%, #aac); }
.re-link:hover { color: var(--a); }
.nc-cta { display: block; text-align: center; text-decoration: none; font-size: 12px; font-weight: 600; padding: 10px; border-radius: 8px; transition: opacity 0.2s; }
.lo-cta { background: var(--a); color: #070b12; box-shadow: 0 0 18px color-mix(in srgb, var(--a) 30%, transparent); }
.lo-cta:hover { opacity: 0.88; }

@media (max-width: 820px) {
  .body-split { grid-template-columns: 1fr; padding: 32px 24px; }
  .nav { padding: 16px 24px; }
  .nav-pill { display: none; }
}
</style>
